/**
 * Client-side DOCX generator for the "envoi général à tous les copropriétaires"
 * mailing. The operator downloads this file, edits it if needed and ships it
 * from their own mailing tool — the application never sends the email itself.
 */

import { AlignmentType, Document, ExternalHyperlink, Packer, Paragraph, TextRun } from 'docx';
import { saveAs } from 'file-saver';

import type { DpeGeneralMailTemplateKey } from './dpeGeneralMailContent';
import { DPE_GENERAL_MAIL_TEMPLATES } from './dpeGeneralMailContent';

interface DpeGeneralMailingDocxParams {
  templateKey: DpeGeneralMailTemplateKey;
  rdvUrl: string;
  referentFullName: string;
  syndicName: string;
  coproName?: string | null;
}

const para = (children: TextRun[] | (TextRun | ExternalHyperlink)[], spacingAfter = 240): Paragraph =>
  new Paragraph({
    alignment: AlignmentType.JUSTIFIED,
    spacing: { after: spacingAfter },
    children,
  });

const bullet = (text: string): Paragraph =>
  new Paragraph({
    bullet: { level: 0 },
    spacing: { after: 60 },
    children: [new TextRun({ text })],
  });

export const generateDpeGeneralMailingDocx = async({
  templateKey,
  rdvUrl,
  referentFullName,
  syndicName,
  coproName,
}: DpeGeneralMailingDocxParams): Promise<void> => {
  const template = DPE_GENERAL_MAIL_TEMPLATES[templateKey];

  const paragraphs: Paragraph[] = [];

  paragraphs.push(
    new Paragraph({
      alignment: AlignmentType.LEFT,
      children: [new TextRun({ text: `Objet : ${template.subject}`, bold: true, size: 24 })],
      spacing: { after: 360 },
    }),
  );

  template.paragraphs.forEach((p) => {
    paragraphs.push(para([new TextRun({ text: p })]));
  });

  template.bulletGroups.forEach((group) => {
    if (group.intro) {
      paragraphs.push(para([new TextRun({ text: group.intro, bold: true })]));
    }
    group.items.forEach((item) => {
      paragraphs.push(bullet(item));
    });
  });

  paragraphs.push(
    para([
      new TextRun({
        text: 'Si vous avez un projet à court ou moyen terme, ou simplement des questions, notre référent mutation-location se tient à votre disposition pour échanger avec vous lors d\'un rendez-vous téléphonique ou en visioconférence.',
      }),
    ]),
  );

  paragraphs.push(
    new Paragraph({
      alignment: AlignmentType.LEFT,
      spacing: { after: 240 },
      children: [
        new TextRun({ text: 'Prendre rendez-vous : ' }),
        new ExternalHyperlink({
          link: rdvUrl,
          children: [new TextRun({ text: rdvUrl, color: '0066CC', underline: {} })],
        }),
      ],
    }),
  );

  paragraphs.push(para([new TextRun({ text: 'Cordialement,' })]));
  paragraphs.push(
    new Paragraph({
      alignment: AlignmentType.LEFT,
      spacing: { after: 60 },
      children: [new TextRun({ text: referentFullName, bold: true })],
    }),
    new Paragraph({
      alignment: AlignmentType.LEFT,
      spacing: { after: 360 },
      children: [
        new TextRun({ text: `Référent Mutation Location${syndicName ? ` — ${syndicName}` : ''}` }),
      ],
    }),
  );

  paragraphs.push(
    new Paragraph({
      alignment: AlignmentType.JUSTIFIED,
      spacing: { after: 240 },
      children: [
        new TextRun({
          text: 'Conformément au RGPD, vous disposez d\'un droit d\'accès, de rectification, d\'effacement et de portabilité de vos données. Vous pouvez exercer ces droits en nous contactant.',
          italics: true,
          size: 18,
        }),
      ],
    }),
  );

  const doc = new Document({
    sections: [{ children: paragraphs }],
  });

  const blob = await Packer.toBlob(doc);
  const safeCoproName = (coproName || 'copropriete').replaceAll(/[^a-zA-Z0-9-_]+/g, '_');
  saveAs(blob, `envoi-general-${templateKey}-${safeCoproName}.docx`);
};
